import type { StepperStep } from "@/design-system/components/ui";
import type { StatusTone } from "@/design-system/components/ui";

// The generic half of the Buyer CRM phase ladders — the presentational rollup of
// a long status enum into a handful of chips, modelled on the Defaults & Workout
// `caseStages.ts` / `DwM1DefaultWorkoutStatusRenderer.StageFor` pair.
//
// There are TWO ladders (buyer leads, submissions) and this file is what stops
// them being two implementations of the same idea. Each ladder is then nothing
// but its data table plus one call, which is what makes the C# twins a
// line-for-line comparison rather than a translation.
//
// 🔴 The rollup is PRESENTATIONAL ONLY. It reads the status; nothing reads it
// back. The state machines, the milestone stamps and the funnel ranks are
// untouched — the full option list in every status-change modal is unchanged,
// and a phase is never persisted, filtered on, or sent to the server.

/** One chip. `statuses` are the raw enum members it rolls up. */
export interface PhaseDef<TStatus extends string> {
  key: string;
  label: string;
  statuses: readonly TStatus[];
}

/**
 * A ladder resolved for one record — everything a render needs and nothing it
 * has to look up for itself.
 *
 * 🔴 `statusLabel` and `tone` are carried HERE, rather than left to the caller,
 * so the raw status badge cannot be omitted. A condensed chip on its own is a
 * lossy read of a record — "Package" does not distinguish a package that is
 * drafted from one that has gone out — so the ladder and the badge are one
 * thing, and `PhaseLadder` renders both from this.
 */
export interface ResolvedPhaseLadder {
  steps: StepperStep[];
  /** Index into `steps`, or −1 when the record is off the ladder. */
  currentIndex: number;
  /** The final rung is reached — the stepper marks it done rather than active. */
  complete: boolean;
  /** The raw status, humanised. Always rendered beside the chips. */
  statusLabel: string;
  tone: StatusTone;
  /**
   * Set only for an off-ladder status. The ladder still renders (unreached), as
   * the commission claim timeline does for Contested/Withdrawn — a record that
   * ended is not a record with no history.
   */
  offLadder: { label: string; note: string } | null;
}

export interface PhaseLadderSpec<TStatus extends string> {
  phases: readonly PhaseDef<TStatus>[];
  /**
   * Statuses that sit OFF the ladder, as an explicit set rather than as
   * "absent from `phases`".
   *
   * 🔴 The distinction is the whole safety property. If off-ladder meant
   * "unmapped", a status added server-side would render as an ended record —
   * the worst possible default, and a silent one. Being explicit means an
   * unmapped status instead degrades to the FIRST phase (visibly too early,
   * never falsely dead) and the completeness test fails until it is placed.
   */
  offLadder: readonly TStatus[];
  /** Humanises a raw status for the badge and the chip hints. */
  labelOf: (status: TStatus) => string;
  toneOf: (status: TStatus) => StatusTone;
  /** Why an off-ladder record is off the ladder — one line, per ladder. */
  offLadderNote: (status: TStatus) => string;
}

export interface PhaseLadder<TStatus extends string> {
  /** The chips, in order. */
  phases: readonly PhaseDef<TStatus>[];
  /** Every status the ladder knows — mapped and off-ladder alike. */
  allStatuses: readonly TStatus[];
  offLadderStatuses: readonly TStatus[];
  isOffLadder: (status: TStatus) => boolean;
  /**
   * The chip a status rolls up to.
   *
   * Degrades to the first phase for anything unrecognised — an unmapped status
   * reads as "only just started", never as an error and never as dead. Off-ladder
   * statuses answer `null`.
   */
  phaseFor: (status: TStatus) => string | null;
  describe: (status: TStatus) => ResolvedPhaseLadder;
}

export function definePhaseLadder<TStatus extends string>(
  spec: PhaseLadderSpec<TStatus>,
): PhaseLadder<TStatus> {
  const { phases, offLadder, labelOf, toneOf, offLadderNote } = spec;

  const indexByStatus = new Map<TStatus, number>();
  phases.forEach((phase, idx) => {
    for (const status of phase.statuses) indexByStatus.set(status, idx);
  });
  const offLadderSet = new Set<TStatus>(offLadder);

  const allStatuses: TStatus[] = [
    ...phases.flatMap((p) => [...p.statuses]),
    ...offLadder,
  ];

  // The hint carries the raw statuses the chip stands for, so hovering a
  // condensed chip answers the question condensing it created.
  const steps: StepperStep[] = phases.map((p) => ({
    key: p.key,
    label: p.label,
    hint: p.statuses.map(labelOf).join(" · "),
  }));

  function phaseFor(status: TStatus): string | null {
    if (offLadderSet.has(status)) return null;
    const idx = indexByStatus.get(status);
    return (idx === undefined ? phases[0] : phases[idx]).key;
  }

  function describe(status: TStatus): ResolvedPhaseLadder {
    const statusLabel = labelOf(status);
    const tone = toneOf(status);

    if (offLadderSet.has(status)) {
      return {
        steps,
        currentIndex: -1,
        complete: false,
        statusLabel,
        tone,
        offLadder: { label: statusLabel, note: offLadderNote(status) },
      };
    }

    const currentIndex = indexByStatus.get(status) ?? 0;
    return {
      steps,
      currentIndex,
      complete: currentIndex === phases.length - 1,
      statusLabel,
      tone,
      offLadder: null,
    };
  }

  return {
    phases,
    allStatuses,
    offLadderStatuses: offLadder,
    isOffLadder: (status) => offLadderSet.has(status),
    phaseFor,
    describe,
  };
}
